import React, { useEffect, useState } from "react";
import { MessageCircle, Mail, Send, Sparkles, Copy, X, Check } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../AuthContext";

export const toWaNumber = (phone) => {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return null;
  if (digits.startsWith("44")) return digits;
  if (digits.startsWith("0")) return `44${digits.slice(1)}`;
  return digits;
};

const subjectFor = (s) => `Re: ${s.service || "cererea ta"} — Marioway Benefits Consultancy Ltd`;

export const ReplyPanel = ({ submission: s, http, onClose, onReplied }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const wa = toWaNumber(s.phone);

  useEffect(() => {
    setLoading(true);
    http.post("/admin/reply-suggest", { submission_id: s.id })
      .then((r) => setText(r.data.text))
      .catch(() => { setText(`Bună ${s.name.split(" ")[0]},\n\nMulțumim pentru mesaj. Te putem ajuta cu partea administrativă. Când ți-ar conveni o discuție inițială gratuită de 15 minute?\n\nFlorin-Marius Bran\nMarioway Benefits Consultancy Ltd`); toast.error("Sugestia AI nu a putut fi generată – am pus un text standard."); })
      .finally(() => setLoading(false));
  }, [s.id, s.name, http]);

  const mark = (channel) => http.post(`/admin/submissions/${s.id}/replied`, { channel }).then(() => onReplied?.(channel)).catch(() => {});

  const sendWhatsApp = () => {
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    mark("whatsapp");
  };
  const openMailApp = () => {
    window.location.href = `mailto:${s.email}?subject=${encodeURIComponent(subjectFor(s))}&body=${encodeURIComponent(text)}`;
    mark("mailto");
  };
  const sendEmail = async () => {
    setSending(true);
    try {
      await http.post("/admin/reply-email", { submission_id: s.id, subject: subjectFor(s), body: text });
      toast.success(`E-mail trimis către ${s.email}`);
      onReplied?.("email");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };
  const copy = () => navigator.clipboard?.writeText(text).then(() => toast.success("Text copiat"));

  return (
    <div className="mt-4 rounded-2xl border border-[#002B7F]/20 bg-[#F5F7FA] p-5" data-testid="reply-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 font-bold text-[#002B7F]"><Sparkles size={16} className="text-[#F37021]" /> Răspuns sugerat de AI</div>
        <button type="button" onClick={onClose} className="text-[#6B7280] hover:text-[#1A1A1A]" aria-label="Închide" data-testid="reply-close"><X size={16} /></button>
      </div>
      <textarea
        className="input-field mt-3 min-h-[180px] bg-white"
        value={loading ? "Se generează răspunsul…" : text}
        onChange={(e) => setText(e.target.value)}
        disabled={loading}
        data-testid="reply-text"
      />
      <p className="mt-2 text-[12px] text-[#6B7280]">Verifică și modifică textul înainte de a-l trimite. Nu include date sensibile.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={sendWhatsApp} disabled={loading || !wa} className="btn-orange text-[13px] px-4 py-2 disabled:opacity-50" data-testid="reply-whatsapp" title={wa ? "" : "Clientul nu a lăsat număr de telefon"}>
          <MessageCircle size={14} /> Trimite pe WhatsApp
        </button>
        <button type="button" onClick={openMailApp} disabled={loading} className="btn-ghost text-[13px] px-4 py-2" data-testid="reply-mailto">
          <Mail size={14} /> Deschide în e-mailul meu
        </button>
        <button type="button" onClick={sendEmail} disabled={loading || sending} className="btn-ghost text-[13px] px-4 py-2 disabled:opacity-50" data-testid="reply-email">
          <Send size={14} /> {sending ? "Se trimite…" : "Trimite e-mail din site"}
        </button>
        <button type="button" onClick={copy} disabled={loading} className="text-[13px] font-semibold text-[#002B7F] px-2 hover:text-[#F37021]" data-testid="reply-copy">
          <Copy size={14} className="inline mr-1" /> Copiază
        </button>
      </div>
      {!wa && <p className="mt-2 text-[12px] text-[#A16207]">Clientul nu a lăsat număr de telefon – folosește e-mailul.</p>}
    </div>
  );
};

export const RepliedBadge = ({ s }) => s.replied_at ? (
  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1F5D2D] bg-[#E8F5EC] rounded-full px-2.5 py-1" data-testid="replied-badge">
    <Check size={12} /> Răspuns trimis ({s.replied_via === "whatsapp" ? "WhatsApp" : "e-mail"})
  </span>
) : null;
